import * as React from "react"
import * as ToastPrimitives from "@radix-ui/react-toast"
import { cva } from "class-variance-authority";
import { X } from "lucide-react"

import { cn } from "../../lib/utils"

const ToastProvider = ToastPrimitives.Provider

const ToastViewport = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
  "fs-toast-viewport fixed top-[88px] left-1/2 -translate-x-1/2 z-[100] flex max-h-screen w-full max-w-[420px] flex-col px-4",
      className
    )}
    {...props} />
))
ToastViewport.displayName = ToastPrimitives.Viewport.displayName

const toastVariants = cva(
  "fs-toast group pointer-events-auto relative flex min-h-[44px] w-full items-center gap-3 overflow-hidden rounded-full border border-glass-strong bg-[rgba(12,13,24,0.92)] pl-9 pr-10 py-2 text-white/85 shadow-[0_14px_34px_rgba(5,7,20,0.45)] transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-90 data-[state=closed]:slide-out-to-top-full data-[state=open]:slide-in-from-top-full before:absolute before:left-3 before:top-1/2 before:h-2.5 before:w-2.5 before:-translate-y-1/2 before:rounded-full",
  {
    variants: {
      variant: {
        info: "before:bg-[#af0e3b]",
        default: "before:bg-[#af0e3b]",
        success: "before:bg-[#22c55e]",
        warning: "before:bg-[#f59e0b]",
        destructive:
          "destructive border-red-500/35 bg-[#19070b] text-red-100 before:bg-[#f97373]",
      },
    },
    defaultVariants: {
      variant: "info",
    },
  }
)

const Toast = React.forwardRef(({ className, variant, ...props }, ref) => {
  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(toastVariants({ variant }), className)}
      {...props} />
  );
})
Toast.displayName = ToastPrimitives.Root.displayName

const ToastAction = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      "inline-flex h-8 shrink-0 items-center justify-center rounded-full border border-glass-strong bg-white/10 px-3 text-sm font-medium text-white/85 transition-colors hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/30 disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-red-200/40 group-[.destructive]:hover:border-red-300/60 group-[.destructive]:hover:bg-red-500/30 group-[.destructive]:hover:text-white group-[.destructive]:focus:ring-red-200/60",
      className
    )}
    {...props} />
))
ToastAction.displayName = ToastPrimitives.Action.displayName

const ToastClose = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      "absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-1 text-white/40 opacity-0 transition-opacity hover:text-white focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-white/20 focus:ring-offset-0 group-hover:opacity-100 group-[.destructive]:text-red-100/70 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-200/60",
      className
    )}
    toast-close=""
    {...props}>
    <X className="h-4 w-4" />
  </ToastPrimitives.Close>
))
ToastClose.displayName = ToastPrimitives.Close.displayName

const ToastTitle = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn("text-sm font-medium text-white", className)}
    {...props} />
))
ToastTitle.displayName = ToastPrimitives.Title.displayName

const ToastDescription = React.forwardRef(({ className, ...props }, ref) => (
  <ToastPrimitives.Description ref={ref} className={cn("text-xs text-white/65 leading-snug", className)} {...props} />
))
ToastDescription.displayName = ToastPrimitives.Description.displayName

export { ToastProvider, ToastViewport, Toast, ToastTitle, ToastDescription, ToastClose, ToastAction };
